import { HideImage, QuestionMark } from '@mui/icons-material'
import { Button, Dialog, DialogContent, Grid } from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { VehicleCheckInBackupCamPhotoDto, vehicleCheckInBackupCamPhotoValidationSchema } from 'dtos'
import { Formik } from 'formik'
import {
  CheckInPhotoBrowseTakeRow,
  CheckInPhotoDialogNavProps,
  CheckInPhotoDialogTitleRow,
  CheckInPhotoPreview,
  CheckInPhotoStackedNavButtons,
  useCheckInPhotoSubmitIntent,
  useLifecycleDocumentPreview
} from './checkInPhotoDialogCommon'

interface BackupCamPhotoDialogProps {
  initialValues: VehicleCheckInBackupCamPhotoDto
  isLoading: boolean
  nav?: CheckInPhotoDialogNavProps
  onClose: () => void
  onDelete: () => void
  onSave: (values: VehicleCheckInBackupCamPhotoDto, options?: { goToNext?: boolean }) => void
  open: boolean
  vehicleGuid: string
}

export default function BackupCamPhotoDialog({
  initialValues,
  isLoading,
  nav,
  onClose,
  onDelete,
  onSave,
  open,
  vehicleGuid
}: BackupCamPhotoDialogProps) {
  const { onFormSubmit, submitClose, submitThenNext } = useCheckInPhotoSubmitIntent(onSave)
  const serverSrc = useLifecycleDocumentPreview(
    vehicleGuid,
    initialValues.documentGuid,
    initialValues.displayFileName,
    controllers.getVehicleCheckInPhotoBase64
  )

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={vehicleCheckInBackupCamPhotoValidationSchema}
        onSubmit={onFormSubmit}
      >
        {({ isSubmitting, setFieldValue, submitForm, values }) => {
          return (
            <form onSubmit={e => void e.preventDefault()}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <CheckInPhotoDialogTitleRow onClose={onClose} title='Backup Cam Photo Details' />

                  <Grid item xs={12}>
                    <CheckInPhotoPreview
                      alt='Backup Cam'
                      file={values.vehicleCheckInBackupCamPhotoFile}
                      placeholderSrc='/9 Backup Cam.jpg'
                      serverSrc={serverSrc}
                    />
                  </Grid>

                  <CheckInPhotoBrowseTakeRow
                    browseInputId='backup-cam-browse'
                    cameraInputId='backup-cam-camera'
                    disabled={isSubmitting || isLoading || values.vehicleCheckInHasBackupCam === false}
                    onFileChosen={file => setFieldValue('vehicleCheckInBackupCamPhotoFile', file)}
                  />

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={
                        isSubmitting ||
                        isLoading ||
                        values.vehicleCheckInHasBackupCam === false ||
                        !Boolean(values.documentGuid)
                      }
                      endIcon={<HideImage />}
                      fullWidth
                      onClick={() => {
                        if (window.confirm('Are you sure you would like to delete the uploaded Backup Cam Photo?')) {
                          onDelete()
                        }
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      color='primary'
                      disabled={isSubmitting || isLoading}
                      endIcon={<QuestionMark />}
                      fullWidth
                      onClick={() => {
                        setFieldValue('vehicleCheckInHasBackupCam', !(values.vehicleCheckInHasBackupCam ?? true))
                      }}
                      variant={values.vehicleCheckInHasBackupCam === false ? 'contained' : 'outlined'}
                    >
                      VEHICLE DOES NOT HAVE BACKUP CAMERA
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || Pressing “Previous” goes to the previous photo without saving. ||
                    </ExplanationAccordion>
                  </Grid>

                  <CheckInPhotoStackedNavButtons
                    isLoading={isLoading}
                    isSubmitting={isSubmitting}
                    nav={nav}
                    submitClose={submitClose}
                    submitForm={submitForm}
                    submitThenNext={submitThenNext}
                  />
                </Grid>
              </DialogContent>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
